import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useFund } from "@/contexts/FundContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard, Building2, UserCog, Users, FileText, ArrowLeftRight,
  BarChart3, Activity, Settings, ScrollText, LifeBuoy, User, Menu, LogOut, ChevronDown, Lock, Shield,
  Sun, Moon, BookOpen, ArrowLeft, Grid3X3, Network, ClipboardCheck, ShieldCheck,
} from "lucide-react";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import API from "@/lib/api";

function complianceGroup(base) {
  return {
    type: "group", label: "AEOI", icon: ShieldCheck, path: base,
    children: [
      { label: "FATCA", icon: FileText, path: `${base}/fatca` },
      { label: "CRS", icon: FileText, path: `${base}/crs` },
    ],
  };
}

function NavGroup({ item, pathname, onNavigate }) {
  const inGroup = pathname.startsWith(item.path);
  const [open, setOpen] = useState(inGroup);
  useEffect(() => { if (inGroup) setOpen(true); }, [inGroup]);
  const slug = item.label.toLowerCase();
  return (
    <div>
      <button type="button" onClick={() => setOpen(o => !o)} className={`sidebar-nav-item w-full ${inGroup ? "text-foreground" : ""}`} data-testid={`nav-${slug}`}>
        <item.icon className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={1.5} />
        <span className="flex-1 text-left">{item.label}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="ml-4 pl-3 border-l border-border/50 space-y-1 mt-1">
          {item.children.map(child => (
            <Link key={child.path} to={child.path} onClick={onNavigate}
              className={`sidebar-nav-item text-sm ${pathname === child.path || pathname.startsWith(`${child.path}/`) ? "active" : ""}`}
              data-testid={`nav-${slug}-${child.label.toLowerCase()}`}>
              <span>{child.label}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function getSuperAdminNav(pathname, role) {
  const portalMatch = pathname.match(/\/super-admin\/funds\/([^/]+)\/portal/);

  if (portalMatch) {
    const fundId = portalMatch[1];
    const base = `/super-admin/funds/${fundId}/portal`;
    return {
      context: "portal",
      fundId,
      items: [
        { label: "Portal Home", icon: LayoutDashboard, path: base },
        { label: "Investors", icon: Users, path: `${base}/investors` },
        { label: "Documents", icon: FileText, path: `${base}/documents` },
        { label: "Transactions", icon: ArrowLeftRight, path: `${base}/transactions` },
        { label: "Reporting", icon: BarChart3, path: `${base}/reporting` },
        role === "super_admin" && complianceGroup(`${base}/compliance`),
        { type: "separator" },
        { label: "Status", icon: Activity, path: `${base}/status` },
        { label: "System Config", icon: Settings, path: `${base}/config` },
        { label: "Statement Templates", icon: FileText, path: `${base}/statement-templates` },
      ].filter(Boolean),
    };
  }

  // Master nav. org_admin sees the same set as super_admin minus
  // "Organizations" (org_admin still owns User Management within their org).
  const isOrgAdmin = role === "org_admin";
  const dashboardPath = isOrgAdmin ? "/org-admin/dashboard" : "/super-admin";
  const masterItems = [
    { label: "Dashboard", icon: LayoutDashboard, path: dashboardPath },
    !isOrgAdmin && { label: "Organizations", icon: Network, path: "/super-admin/organizations" },
    { label: "Fund Management", icon: Building2, path: "/super-admin/funds" },
    { label: "User Management", icon: UserCog, path: "/super-admin/admins" },
    !isOrgAdmin && { label: "Roles & Permissions", icon: ShieldCheck, path: "/super-admin/roles-permissions" },
    { label: "Onboarding", icon: ClipboardCheck, path: "/super-admin/onboarding" },
    { type: "separator" },
    { label: "Support", icon: LifeBuoy, path: "/super-admin/support" },
    { label: "Fund Oversight", icon: Shield, path: "/super-admin/governance" },
    { label: "Audit Logs", icon: ScrollText, path: "/super-admin/audit" },
  ].filter(Boolean);

  return {
    context: "master",
    fundId: null,
    items: masterItems,
  };
}

const NAV_CONFIG = {
  admin: [
    { label: "Dashboard", icon: LayoutDashboard, path: "/admin" },
    { label: "Fund Profile", icon: Building2, path: "/admin/fund" },
    { label: "Investors", icon: Users, path: "/admin/investors" },
    { label: "Documents", icon: FileText, path: "/admin/documents" },
    { label: "Transactions", icon: ArrowLeftRight, path: "/admin/transactions" },
    { label: "Reporting", icon: BarChart3, path: "/admin/reporting" },
    complianceGroup("/admin/compliance"),
    { type: "separator" },
    { label: "Support", icon: LifeBuoy, path: "/admin/support" },
  ],
  investor: [
    { label: "Dashboard", icon: LayoutDashboard, path: "/investor" },
    { label: "Profile", icon: User, path: "/investor/profile" },
    { label: "Documents", icon: FileText, path: "/investor/documents" },
    { label: "Transactions", icon: ArrowLeftRight, path: "/investor/transactions" },
    { label: "Reporting", icon: BarChart3, path: "/investor/reporting" },
    { type: "separator" },
    { label: "Support", icon: LifeBuoy, path: "/investor/support" },
  ],
  prospective_client: [
    { label: "Dashboard", icon: LayoutDashboard, path: "/prospective" },
    { label: "Onboarding Form", icon: ClipboardCheck, path: "/onboarding" },
    { type: "separator" },
    { label: "Support", icon: LifeBuoy, path: "/support" },
  ],
};

const ROLE_LABELS = {
  super_admin: "Super Admin",
  org_admin: "Org Admin",
  admin: "Fund Manager",
  investor: "Investor",
  prospective_client: "Fund / Asset Manager",
};

function SidebarContent({ items, pathname, context, fundId, activeFund, userRole, branding, onNavigate }) {
  const isMaster = context === "master";
  const isPortal = context === "portal";

  // Determine correct logo navigation target
  const logoTarget = userRole === "super_admin"
    ? "/super-admin"
    : userRole === "org_admin"
      ? "/org-admin/dashboard"
      : userRole === "admin"
        ? "/admin"
        : "/investor";

  // Use admin branding for investor portal if available
  const showBranding = userRole === "investor" && branding?.admin_name;
  const logoSrc = showBranding && branding?.admin_logo
    ? `${process.env.REACT_APP_BACKEND_URL}/api/config/admin-logo`
    : "/tychi-icon.svg";
  const logoLabel = showBranding ? branding.admin_name : isPortal ? "Tychi Portal" : isMaster ? "Tychi Master" : "Tychi";

  return (
    <div className="flex flex-col h-full">
      <div className="p-6 pb-4">
        <Link to={logoTarget} className="flex items-center gap-3" data-testid="sidebar-logo" onClick={onNavigate}>
          <img src={logoSrc} alt="Logo" className="w-9 h-9 rounded-lg object-contain" />
          <span className="font-heading text-xl font-bold text-foreground tracking-tight">
            {logoLabel}
          </span>
        </Link>
        {showBranding && branding?.admin_website && (
          <p className="text-[10px] text-muted-foreground mt-1 pl-12">{branding.admin_website}</p>
        )}
        {isPortal && activeFund && (
          <div className="mt-3 px-1">
            <Badge variant="secondary" className="text-[10px] uppercase tracking-wider w-full justify-center py-1">
              {activeFund.name}
            </Badge>
          </div>
        )}
      </div>
      <Separator className="opacity-50" />

      {/* Back navigation for portal context */}
      {isPortal && fundId && (
        <div className="px-4 pt-3 space-y-1">
          <Link
            to={`/super-admin/funds/${fundId}/apps`}
            className="sidebar-nav-item text-xs"
            data-testid="nav-back-to-apps"
            onClick={onNavigate}
          >
            <Grid3X3 className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={1.5} />
            <span>Back to Apps</span>
          </Link>
          <Link
            to="/super-admin"
            className="sidebar-nav-item text-xs"
            data-testid="nav-back-to-master"
            onClick={onNavigate}
          >
            <ArrowLeft className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={1.5} />
            <span>Tychi Master</span>
          </Link>
          <Separator className="opacity-50 !mt-2" />
        </div>
      )}

      <nav className="flex-1 p-4 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
        {items.map((item, i) =>
          item.type === "separator" ? (
            <Separator key={i} className="my-3 opacity-50" />
          ) : item.type === "group" ? (
            <NavGroup key={item.path} item={item} pathname={pathname} onNavigate={onNavigate} />
          ) : (
            <Link
              key={item.path}
              to={item.path}
              className={`sidebar-nav-item ${pathname === item.path ? "active" : ""}`}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={onNavigate}
            >
              <item.icon className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={1.5} />
              <span>{item.label}</span>
            </Link>
          )
        )}
      </nav>
    </div>
  );
}

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Desktop drawer is open by default so first paint is identical to the
  // pre-enhancement layout. The hamburger in the header toggles it on ≥lg,
  // and any nav-link click auto-closes it via `closeDrawers` below.
  const [desktopOpen, setDesktopOpen] = useState(true);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [branding, setBranding] = useState(null);

  const { activeFund } = useFund();

  // Fetch admin branding for investor portal
  useEffect(() => {
    if (user?.role === "investor") {
      API.get("/config/platform").then(res => setBranding(res.data)).catch(() => {});
    }
  }, [user?.role]);

  let navItems, navContext, navFundId;

  if (user?.role === "super_admin" || user?.role === "org_admin") {
    const nav = getSuperAdminNav(pathname, user.role);
    navItems = nav.items;
    navContext = nav.context;
    navFundId = nav.fundId;
  } else {
    navItems = NAV_CONFIG[user?.role] || [];
    navContext = "default";
    navFundId = null;
  }

  const initials = user?.name?.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) || "?";

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Auto-close both drawer states whenever a destination is selected.
  // Runs on every <Link onClick> inside SidebarContent.
  const closeDrawers = () => {
    setMobileOpen(false);
    setDesktopOpen(false);
  };

  // Single hamburger button in the header handles both breakpoints.
  // On lg+ it toggles the desktop <aside>; on smaller it toggles the mobile Sheet.
  const toggleDrawer = () => {
    const isDesktop = typeof window !== "undefined"
      && window.matchMedia("(min-width: 1024px)").matches;
    if (isDesktop) {
      setDesktopOpen((o) => !o);
    } else {
      setMobileOpen((o) => !o);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-layout">
      {/* Desktop sidebar — visible on ≥lg when desktopOpen is true */}
      <aside
        className={`fixed left-0 top-0 h-full w-64 border-r border-border bg-card/60 dark:bg-card/60 glass z-30 hidden ${desktopOpen ? "lg:block" : ""}`}
        data-testid="desktop-sidebar"
      >
        <SidebarContent items={navItems} pathname={pathname} context={navContext} fundId={navFundId} activeFund={activeFund} userRole={user?.role} branding={branding} onNavigate={closeDrawers} />
      </aside>

      {/* Mobile / tablet sidebar — Sheet controlled by mobileOpen */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-64 p-0 bg-card border-border lg:hidden">
          <SidebarContent items={navItems} pathname={pathname} context={navContext} fundId={navFundId} activeFund={activeFund} userRole={user?.role} branding={branding} onNavigate={closeDrawers} />
        </SheetContent>
      </Sheet>

      {/* Main content area — reclaims full width when the desktop drawer is closed */}
      <div className={desktopOpen ? "lg:ml-64" : ""}>
        {/* Header */}
        <header className="sticky top-0 z-20 h-16 border-b border-border bg-background/80 glass flex items-center justify-between px-4 sm:px-6 lg:px-8" data-testid="dashboard-header">
          {/* Manual toggle — always visible across all breakpoints */}
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleDrawer}
            className="w-9 h-9"
            data-testid="drawer-toggle-button"
            aria-label="Toggle navigation"
          >
            <Menu className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="w-9 h-9 rounded-full hover:bg-secondary/80 transition-colors"
              data-testid="theme-toggle-btn"
            >
              {theme === "dark" ? <Sun className="w-[18px] h-[18px] text-amber-400" /> : <Moon className="w-[18px] h-[18px] text-slate-600" />}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-testid="user-menu-trigger">
                  <Avatar className="w-8 h-8 bg-secondary border border-border">
                    <AvatarFallback className="text-xs font-medium bg-secondary text-foreground">{initials}</AvatarFallback>
                  </Avatar>
                  <div className="hidden sm:block text-left">
                    <p className="text-sm font-medium text-foreground leading-none">{user?.name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{ROLE_LABELS[user?.role]}</p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-muted-foreground hidden sm:block" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-card border-border">
                <div className="px-3 py-2">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                  <Badge variant="secondary" className="mt-1.5 text-[10px] uppercase tracking-wider">
                    {ROLE_LABELS[user?.role]}
                  </Badge>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setShowChangePassword(true)} data-testid="menu-change-password">
                  <Lock className="w-4 h-4 mr-2" />
                  Change Password
                </DropdownMenuItem>
                {user?.role !== "prospective_client" && (
                  <DropdownMenuItem
                    onClick={() => {
                      const prefix = { super_admin: "/super-admin", org_admin: "/super-admin", admin: "/admin", investor: "/investor" };
                      navigate(`${prefix[user?.role] || "/investor"}/knowledge-bank`);
                    }}
                    data-testid="menu-knowledge-bank"
                  >
                    <BookOpen className="w-4 h-4 mr-2" />
                    Knowledge Bank
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-400 focus:text-red-400" data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6 lg:p-8" data-testid="dashboard-content">
          {children}
        </main>
      </div>

      <ChangePasswordDialog open={showChangePassword} onOpenChange={setShowChangePassword} />
    </div>
  );
}
